import { IpgHelperWithdrawStatusEnum } from "@payping/api";
import {
  CheckCircleFilled,
  ClockCircleFilled,
  Close,
  CloseCircleFilled,
  CuToman,
  DownOutlined,
  FeatherLeft,
  LeftOutlined,
  SettingOutlined,
  ShopOutlined,
} from "@payping/icons";
import { formatLocalizedNumber } from "@payping/utilities-shared/number";
import { ensureString } from "@payping/utilities-shared/string";
import { DashboardRoutes } from "@/router/constant";
import {
  Avatar,
  Button,
  Col,
  Divider,
  Drawer,
  Flex,
  LegacyCard,
  Popover,
  Row,
  Tag,
  Typography,
  useMessagedAsync,
  usePaypingContext,
} from "@payping/web-components";
import { Link, useLocation } from "react-router-dom";
import { getBalance, getUserWizardInfo } from "@payping/redux/actions";
import { useAppDispatch, useAppSelector } from "@/utils/redux";
import { useAppTranslation } from "@/utils/useAppTranslation";
import { overviewTourIds } from "@/assets/tourIds";
import { dateFormats, jalaliNow, moment, toJalali } from "@payping/utils/dateUtils";
import { useEffect, useState } from "react";
import { BalancePopover } from "@/refund/components";
import { useImmerAtom } from "jotai-immer";
import { coreAtom } from "@/core/state/root";
import { dashboardPreferencesAtom } from "@/core/state";
import { selectAtom } from "jotai/utils";
import { useAtom } from "jotai";
import { useGetLastWithdrawByStatus, useGetPendingWithdrawAmount } from "@/pages/Sale/Withdraw/_hooks";

enum UserPlans {
  Basic = "پایه",
}

const languageAtom = selectAtom(dashboardPreferencesAtom, (val) => val.language);

export const BusinessSection = () => {
  const { t } = useAppTranslation();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [openPopover, setOpenPopover] = useState(false);
  const [, setCoreState] = useImmerAtom(coreAtom);
  const [language] = useAtom(languageAtom);
  const pendingWithdrawState = useGetPendingWithdrawAmount();
  const lastWithdarwState = useGetLastWithdrawByStatus(IpgHelperWithdrawStatusEnum.Succeed);

  const dispatch = useAppDispatch();
  const personalInfo = useAppSelector((state) => state.user.personalInfo);
  const { breakpoints } = usePaypingContext();
  const { lg } = breakpoints;

  const currentPlan = useAppSelector((state) => state.user?.panelInfo?.currentPlan);

  const username = `@${ensureString({
    text: personalInfo?.username ?? "",
    fallbackValue: "-",
  })}`;
  const fullName = ensureString({
    text:
      personalInfo?.businessName ||
      `${ensureString({
        text: personalInfo?.firstName ?? "",
        fallbackValue: "-",
      })} ${ensureString({
        text: personalInfo?.lastName ?? "",
        fallbackValue: "-",
      })}`,
    fallbackValue: "-",
  });

  useMessagedAsync(() => dispatch(getUserWizardInfo()), [dispatch], { useProgress: true });
  const { value: balance } = useMessagedAsync(() => dispatch(getBalance()), [dispatch], {
    success: false,
    useProgress: true,
  });

  const { pathname } = useLocation();

  // Close the drawer whenever the URL changes
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setOpenPopover(false);
    setIsDrawerOpen(false);
  }, [pathname]);

  const lastWithdrawTime = moment
    .duration(
      toJalali({ date: lastWithdarwState.value?.reqDate, toFormat: dateFormats.ui.jalaliDateTime }).object?.diff(
        jalaliNow().object,
      ),
    )
    .locale(language)
    .humanize(true);

  const headerContents = (
    <Flex vertical justify={"center"} align={"center"} gap={"24px"}>
      {/* < ایکون کسب کار > */}
      <Avatar
        className="background--gray100 tw:sm:mt-5! tw:md:mt-3!"
        size={72}
        src={<ShopOutlined width={33} color={"gray"} />}
      />

      {/* < ایکون کسب کار /> */}

      {/* < نام کسب کار & وضعیت ترمینال > */}
      <Flex vertical gap={"8px"} justify={"center"} align={"center"} className="tw:w-full">
        <Typography.Title level={5} className={"tw:m-0!"}>
          {fullName || username}
        </Typography.Title>

        <Flex vertical gap={"12px"} justify={"center"} align={"center"}>
          {currentPlan?.title && currentPlan.title !== UserPlans.Basic && (
            <Tag className={"tw:text-center"}>{currentPlan.title}</Tag>
          )}

          {personalInfo?.haveTerminal ? (
            <Flex gap={"small"} align={"center"}>
              <CheckCircleFilled width={14} color={"green"} className={"tw:flex tw:items-center"} />
              <Typography.Text className={"font-bold"}>{t("mainNav.userInfo.activeTerminal")}</Typography.Text>
            </Flex>
          ) : (
            <Flex gap={"small"}>
              <CloseCircleFilled width={14} color={"red"} className={"tw:flex tw:items-center"} />
              <Typography.Text className={"font-bold"}>{t("mainNav.userInfo.inactiveTerminal")}</Typography.Text>
            </Flex>
          )}
        </Flex>
      </Flex>
      {/* < نام کسب کار & وضعیت ترمینال /> */}
    </Flex>
  );

  const balanceAndSettle = (
    <Flex align="center">
      <Typography.Title
        level={5}
      >{`${formatLocalizedNumber({ value: balance ?? 0, options: { maximumFractionDigits: 2 } })}`}</Typography.Title>
      <CuToman width={24} height={24} />
    </Flex>
  );

  const desktopContent = (
    <div className="tw:my-5! tw:w-[340px]! tw:px-4!" data-tourId={overviewTourIds.businessSettingDropdown}>
      {/* < Header > */}
      {headerContents}
      {/* </ Header > */}

      <Divider className="tw:my-4!" />
      <Row className={"tw:w-full!"}>
        <Col xs={24}>
          <Flex vertical className={"tw:w-full!"} gap={"middle"}>
            <Typography.Text type={"secondary"}>{t("balance")}</Typography.Text>
            {balanceAndSettle}
            {!Number.isNaN(pendingWithdrawState.value) ? (
              <LegacyCard.Ant className="background--gray100" loading={lastWithdarwState.loading}>
                <Flex wrap={"wrap"} vertical className={"tw:w-full!"} gap={"small"}>
                  <div className={"tw:flex tw:w-full! tw:justify-between"}>
                    <Flex gap={"small"}>
                      <ClockCircleFilled width={14} color={"orange"} className={"tw:flex tw:items-center"} />
                      <Typography.Text ellipsis strong>
                        {t("mainNav.userInfo.inProcessWithdraw")}
                      </Typography.Text>
                    </Flex>
                    <Flex align="center">
                      <Typography.Title
                        level={5}
                      >{`${formatLocalizedNumber({ value: pendingWithdrawState.value ?? 0, options: { maximumFractionDigits: 2 } })}`}</Typography.Title>
                      <CuToman width={24} height={24} />
                    </Flex>
                  </div>
                  <Typography.Text type={"secondary"}>{t("mainNav.userInfo.inProcessDesc")}</Typography.Text>
                </Flex>
              </LegacyCard.Ant>
            ) : null}
          </Flex>
        </Col>
        {lastWithdarwState?.value ? (
          <>
            <Divider className="tw:my-4!" />
            <Col xs={24}>
              <div className={"tw:flex tw:w-full! tw:justify-between"}>
                <Typography.Text strong>{`${t("mainNav.userInfo.lastWithdraw")} ${lastWithdrawTime}`}</Typography.Text>
                <Link to={DashboardRoutes.mySale.balanceAndWithdraw.url}>{t("mainNav.userInfo.allWithdraws")}</Link>
              </div>
            </Col>
          </>
        ) : null}
      </Row>
      <Divider className="my-1" />
      <BalancePopover />
      <Divider className="tw:my-4!" />
      <Link
        to={DashboardRoutes.settings.root.url}
        onClick={() => {
          setCoreState((draft) => {
            // eslint-disable-next-line no-param-reassign
            draft.firstUrlToSettingsAndProfile = pathname;
          });
        }}
        className="text--initial tw:w-full"
      >
        <Flex justify={"space-between"}>
          <Flex gap={"small"}>
            <SettingOutlined width={"1em"} />
            {t("mainNav.userInfo.settings")}
          </Flex>
          <LeftOutlined width={12} />
        </Flex>
      </Link>
    </div>
  );

  const mobileContent = (
    <Drawer
      key={location.pathname}
      open={isDrawerOpen}
      placement={"top"}
      height={"100%"}
      title={t("mainNav.userInfo.settingsDrawerTitle")}
      closeIcon={false}
      // because MainNavMobile z-index is 2004
      zIndex={2005}
      data-tourId={overviewTourIds.businessSettingDropdown}
      extra={
        <Button
          type={"text"}
          icon={<Close width={14} height={14} />}
          onClick={() => {
            setIsDrawerOpen(false);
          }}
        />
      }
    >
      <Row gutter={[0, 0]} className={"tw:w-full!"}>
        <Col span={24}>{headerContents}</Col>
        <Divider className="tw:my-4!" />
        <Row gutter={[0, 0]} className={"tw:w-full!"}>
          <Col xs={24}>
            <Flex vertical className={"tw:w-full!"} gap={"middle"}>
              {/* < موجودی > */}
              <Typography.Text type={"secondary"}>{t("balance")}</Typography.Text>

              {/* < عدد موجودی و برداشت > */}
              {balanceAndSettle}

              {/* < کارت درحال تسویه حساب ?? > */}
              {!Number.isNaN(pendingWithdrawState.value) ? (
                <LegacyCard.Ant className={`background--gray100`} loading={lastWithdarwState.loading}>
                  <Flex wrap={"wrap"} vertical className={"tw:w-full!"} gap={"12px"}>
                    <Flex justify={"space-between"}>
                      {/* < متن درحال تسویه & ایکون ساعت > */}
                      <Flex gap={"small"}>
                        <ClockCircleFilled width={14} color={"orange"} className={"tw:flex tw:items-center"} />
                        <Typography.Text ellipsis strong>
                          {t("mainNav.userInfo.inProcessWithdraw")}
                        </Typography.Text>
                      </Flex>

                      {/* < مبلغ تسویه > */}
                      <Flex align="center">
                        <Typography.Title
                          level={5}
                        >{`${formatLocalizedNumber({ value: pendingWithdrawState.value ?? 0, options: { maximumFractionDigits: 2 } })}`}</Typography.Title>
                        <CuToman width={24} height={24} />
                      </Flex>
                    </Flex>

                    {/* < توضیحات پایین > */}
                    <Typography.Text type={"secondary"} className={"tw:font-medium"}>
                      {t("mainNav.userInfo.inProcessDesc")}
                    </Typography.Text>
                  </Flex>
                </LegacyCard.Ant>
              ) : null}
            </Flex>
          </Col>
          {lastWithdarwState.value ? (
            <>
              <Divider className="tw:my-4!" />
              <Col xs={24}>
                <div className={"tw:flex tw:w-full! tw:justify-between"}>
                  <Typography.Text
                    strong
                  >{`${t("mainNav.userInfo.lastWithdraw")} ${lastWithdrawTime}`}</Typography.Text>
                  <Link to={DashboardRoutes.mySale.balanceAndWithdraw.url} className={"tw:font-medium"}>
                    {t("mainNav.userInfo.allWithdraws")}
                  </Link>
                </div>
              </Col>
            </>
          ) : null}
        </Row>
        <Divider className="tw:my-4!" />
        <BalancePopover />
        <Divider className="tw:my-4!" />
        <Link
          to={DashboardRoutes.settings.root.url}
          onClick={() => {
            setCoreState((draft) => {
              // eslint-disable-next-line no-param-reassign
              draft.firstUrlToSettingsAndProfile = pathname;
            });
          }}
          className="text--initial w-full"
        >
          <Flex justify={"space-between"}>
            <Flex gap={"small"}>
              <SettingOutlined width={"1em"} />
              {t("mainNav.userInfo.settings")}
            </Flex>
            <FeatherLeft width={14} />
          </Flex>
        </Link>
      </Row>
    </Drawer>
  );
  // const popoverContent = lg ? desktopContent : mobileContent;

  const popoverAnchor = (
    <Flex gap={"small"}>
      <Avatar className="background--gray100" src={<ShopOutlined width={14} color={"gray"} />} />
      <div
        data-tourId={overviewTourIds.businessSettingAnchor}
        onClick={() => {
          if (!lg) setIsDrawerOpen(true);
        }}
      >
        <Button type={"text"}>
          <Flex gap={"8px"} align={"center"}>
            <Typography.Text className={"tw:font-medium"} ellipsis>
              {fullName || username}
            </Typography.Text>
            <DownOutlined className={"tw:flex tw:items-center"} width={9} />
          </Flex>
        </Button>
      </div>
    </Flex>
  );

  // return { useInfo: { content: popoverContent, anchor: popoverAnchor } };

  return lg ? (
    <Popover
      zIndex={999}
      placement={"bottomLeft"}
      content={desktopContent}
      onOpenChange={(val) => {
        setOpenPopover(val);
      }}
      key={pathname}
      open={openPopover}
    >
      {popoverAnchor}
    </Popover>
  ) : (
    <>
      {popoverAnchor}
      {mobileContent}
    </>
  );
};
